import { ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { IBaseWidgetConfig } from '../widget/base/widget-list';
import { BaseWidgetComponent } from '../widget/base/base-widget';
import { DxButtonModule, DxNumberBoxComponent, DxNumberBoxModule } from 'devextreme-angular';
import { CommonModule } from '@angular/common';
import { WidgetDataService } from '../widget/data/widget-data.service';
import { RemoteDataWrapperService } from 'src/app/shared/services/remote-data/remote-data-wrapper.service';
import { DashboardDataService } from '../../data/dashboard-data.service';
import { combineLatest, Observable, of, Subject, Subscription, takeUntil } from 'rxjs';
import { MachineFunction } from 'src/app/shared/services/remote-data/machine-one.service';
import { ToastrService } from 'ngx-toastr';
import { AuthHelperService } from 'src/app/core/auth/auth-helper.service';
import { MachineInfoMainService } from '../../../../shared/services/machine-info.service';
import { confirm } from 'devextreme/ui/dialog';
import { TranslateWithFallbackPipe } from 'acl-cloud-lib';

/**
 * @description Adjust Panel Widget Config. Contains all data needed to render the widget.
 */
export interface IAdjustPanelWidgetConfig extends IBaseWidgetConfig {
  sectionIndex: number | null;
}

interface AdjustPanelData {
  sectionName: string;
  vibrator: { flowSetup: number; speedSet: number; actualFlow: number; }
  slides: Array<{
    slideName: string;
    speed: number;
    actualFlow: number;
    productFlowSetup: number;
    productFlowSetupFromSection: number;
    speedManualVis?: string;
    speedFixFlowVis?: string;
    speedSectionFixFlowVis?: string;
    speedPropId?: number;
    productFlowSetupPropId?: number;
    productFlowSetupMinMax?: MinMax;
    speedSetupMinMax?: MinMax;
    speedSetupValueStore?: number;
    productFlowSetupValueStore?: number;
    speedSetupDisabled: boolean;
    productFlowSetupDisabled: boolean;
    speedSetupRole?: string;
  }>
  camera: { delay: number; minTimeOn: number; growH: number; growV: number; }
}

interface MinMax {
  min: number;
  max: number;
}

@Component({
  selector: 'app-adjust-panel',
  standalone: true,
  imports: [DxButtonModule, DxNumberBoxModule, CommonModule],
  templateUrl: './adjust-panel.component.html',
  styleUrl: './adjust-panel.component.scss'
})
export class AdjustPanelWidgetComponent extends BaseWidgetComponent<IAdjustPanelWidgetConfig> {

  protected viewData: AdjustPanelData = {
    sectionName: 'Section 1',
    vibrator: {
      flowSetup: 0,
      actualFlow: 0,
      speedSet: 0
    },
    slides: [
      {
        slideName: 'Slide 1',
        speed: 0,
        actualFlow: 0,
        productFlowSetup: 0,
        productFlowSetupFromSection: 0,
        speedSetupDisabled: true,
        productFlowSetupDisabled: true
      },
    ],
    camera: {
      delay: 0,
      minTimeOn: 0,
      growH: 0,
      growV: 0,
    }
  };

  protected availableActions: MachineFunction[] = [];

  protected isEmpting: boolean = false;
  protected isRunning: boolean = false;

  protected speedManualVisibility: string = '';
  protected speedFixFlowVisibility: string = '';

  // ID of property setters
  protected _emptyingPropertyId?: number;
  protected flowSetupPropertyId?: number;
  protected speedSetPropertyId?: number;
  protected shiftPropId?: number;
  protected minShootPropId?: number;
  protected blobHAdjustPropId?: number;
  protected blobVAdjustPropId?: number;

  private readonly _unSubscriber: Subject<any> = new Subject();

  // flags for disable inputs
  protected productFlowInputDisabled: boolean = true;
  protected speedSetInputDisabled: boolean = true;
  protected shiftInputDisabled: boolean = true;
  protected minShootInputDisabled: boolean = true;
  protected blobHAdjustInputDisabled: boolean = true;
  protected blobVAdjustInputDisabled: boolean = true;

  // properties for setting old value if error 
  protected flowSetupValueStore: number = 0;
  protected speedSetValueStore: number = 0;
  protected shiftValueStore: number = 0;
  protected minShootValueStore: number = 0;
  protected blobHAdjustValueStore: number = 0;
  protected blobVAdjustValueStore: number = 0;

  // min max prop values
  protected flowSetupMinMax?: MinMax;
  protected speedSetMinMax?: MinMax;
  protected shiftMinMax?: MinMax;
  protected minShootMinMax?: MinMax;
  protected blobHMinMax?: MinMax;
  protected blobVMinMax?: MinMax;


  // roles for editing
  protected shiftRole?: string;
  protected minShootRole?: string;
  protected blobHRole?: string;
  protected blobVRole?: string;

  @ViewChild('flowSetupInput') flowSetupInput?: DxNumberBoxComponent;
  @ViewChild('shiftInput') shiftInput?: DxNumberBoxComponent;
  @ViewChild('minShootInput') minShootInput?: DxNumberBoxComponent;
  @ViewChild('blobHInput') blobHInput?: DxNumberBoxComponent;
  @ViewChild('blobVInput') blobVInput?: DxNumberBoxComponent;

  @ViewChildren('speedInput') speedInputs?: QueryList<DxNumberBoxComponent>;
  @ViewChildren('productFlowSetup') productFlowSetupInputs?: QueryList<DxNumberBoxComponent>;

  constructor(
    widgetDataService: WidgetDataService<IAdjustPanelWidgetConfig>,
    remoteDataWrapperService: RemoteDataWrapperService,
    private readonly _dashboardDataSvc: DashboardDataService,
    private readonly _toastr: ToastrService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _authHelperSvc: AuthHelperService,
    private readonly _translate: TranslateWithFallbackPipe,
  ) {
    super(widgetDataService, remoteDataWrapperService);
  }

  private _combineSub?: Subscription;

  protected getCmdRole(cmd: string): string | undefined {
    return this.availableActions.find(c => c.name === cmd)?.role;
  }

  protected override setOptions(options: IAdjustPanelWidgetConfig | undefined): void {
    if (options && options.machineName) {
      const machineStructureRaw$ = this.remoteDataWrapperService.getMachineStructureRaw(options.machineName ?? '');
      const machineStructure$ = this.remoteDataWrapperService.getMachineStructure(options.machineName ?? '');

      this.options = { ...options };

      this._combineSub?.unsubscribe();
      this._combineSub = combineLatest([machineStructure$, machineStructureRaw$]).pipe(
        takeUntil(this._unSubscriber)
      ).subscribe(([machineStructure, machineStructureRaw]) => {
        if (machineStructure && machineStructureRaw && machineStructureRaw !== '{}') {
          try {
            machineStructureRaw = JSON.parse(machineStructureRaw);
            this._setActualDataFromStructure(machineStructureRaw, machineStructure);
          } catch (e) {
            console.error('Error parsing json machine structure', e);
          }
        }
      });
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this._dashboardDataSvc.widgetInitialized();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._unSubscriber.next(null);
    this._unSubscriber.complete();
    this._cd.detach();
    this._dashboardDataSvc.widgetDestroyed();
  }

  startConfirmDialog(): void {
    const result = confirm(this._translate.transform('start-machine-question'), this._translate.transform('confirmation'));
    result.then((dialogResult) => {
      if (dialogResult) {
        this.doCommand('RunCmd');
      }
    });
  }

  protected async doCommand(cmdName: 'RunCmd' | 'StopCmd'): Promise<void> {
    const cmd = this.availableActions.find(cmd => cmd.name === cmdName);
    if (cmd) {
      if (this.options?.machineName) {
        const res = await this.remoteDataWrapperService.invokeOnSupplierById(this.options.machineName, cmd.id, null);

        if (res) {
          this._toastr.success('Operation done.');
        } else {
          this._toastr.error('Operation failed.');
        }
      }
    } else {
      this._toastr.error('Operation failed.');
    }
  }

  protected async setEmptyOnSupplier(val: boolean): Promise<void> {
    if (this._emptyingPropertyId && this.options?.machineName) {
      const res = await this.remoteDataWrapperService.setPropertyOnSupplier(this.options.machineName, this._emptyingPropertyId, val);

      if (res) {
        this._toastr.success('Operation done.');
        return;
      }
    }

    this._toastr.error('Operation failed.');
  }

  private _structureSubs: Subscription[] = [];

  private _setActualDataFromStructure(dataRaw: any, data: any): void {
    this._structureSubs.forEach(sub => sub.unsubscribe());
    this._structureSubs = [];
    this.availableActions = [];
    this.viewData.slides = [];

    if (this.options && this.options.sectionIndex !== null) {
      // Set section name
      const currentSectionRaw = dataRaw.MachineOne.Sections._V[this.options.sectionIndex];
      const currentSection = data.MachineOne.Sections[this.options.sectionIndex];

      if (!currentSection || !currentSectionRaw) return;

      this.viewData.sectionName = currentSectionRaw._L;


      if (Number.isInteger(currentSectionRaw.EmptyingOnOff._ID)) {
        this._emptyingPropertyId = currentSectionRaw.EmptyingOnOff._ID;
      }

      if (this._emptyingPropertyId) {
        this._structureSubs.push(
          currentSection.EmptyingOnOff.pipe(takeUntil(this._unSubscriber)).subscribe((val: boolean) => {
            this.isEmpting = val;
          })
        );
      }

      this._structureSubs.push(
        currentSection.IsRunning.pipe(takeUntil(this._unSubscriber)).subscribe((val: boolean) => {
          this.isRunning = val;
        })
      );

      // Initialize functions
      Object.keys(currentSectionRaw).forEach(key => {
        if (this.options?.sectionIndex != null) {
          if (typeof currentSectionRaw[key] === 'object' &&
            Object.keys(currentSectionRaw[key]).includes('_T') &&
            currentSectionRaw[key]['_T'] === 'CMD'
          ) {
            const func: any = currentSectionRaw[key];
            this.availableActions.push({ id: func._ID, name: key, args: null, role: func.Role })
          }
        }
      });

      // Set vibrators
      this._structureSubs.push(
        currentSection.PortataKgOra.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.vibrator.actualFlow = val;
        })
      );

      this.flowSetupPropertyId = currentSectionRaw.ProductFlowSetup._ID;
      this.speedFixFlowVisibility = currentSectionRaw.SpeedFixFlowVisibility._V;
      this.flowSetupMinMax = {
        min: currentSectionRaw.ProductFlowSetup.MIN,
        max: currentSectionRaw.ProductFlowSetup.MAX
      };

      this._structureSubs.push(
        currentSection.ProductFlowSetup.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.vibrator.flowSetup = val;
          this.flowSetupValueStore = val;
          this.productFlowInputDisabled = false;
        })
      );

      this._structureSubs.push(
        currentSection.SpeedFixFlowVisibility.pipe(takeUntil(this._unSubscriber)).subscribe((val: string) => {
          this.speedFixFlowVisibility = val;
        })
      );

      this.speedSetPropertyId = currentSectionRaw.SpeedSet._ID;
      this.speedManualVisibility = currentSectionRaw.SpeedManualVisibility._V;
      this.speedSetMinMax = {
        min: currentSectionRaw.SpeedSet.MIN,
        max: currentSectionRaw.SpeedSet.MAX
      };

      this._structureSubs.push(
        currentSection.SpeedSet.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.vibrator.speedSet = val;
          this.speedSetValueStore = val;
          this.speedSetInputDisabled = false;
        })
      );

      this._structureSubs.push(
        currentSection.SpeedManualVisibility.pipe(takeUntil(this._unSubscriber)).subscribe((val: string) => {
          this.speedManualVisibility = val;
        })
      );

      // Set slides
      // 
      currentSectionRaw.Slides._V.forEach((slide: any, index: number) => {
        this.viewData.slides.push({
          slideName: slide._L,
          speed: (slide.Vibrator.SpeedSetup._V as number) ?? 0,
          speedSetupMinMax: { min: slide.Vibrator.SpeedSetup.MIN, max: slide.Vibrator.SpeedSetup.MAX },
          actualFlow: (slide.PortataMediaKgOra._V as number) ?? 0,
          productFlowSetup: (slide.ProductFlowSetup._V as number) ?? 0,
          productFlowSetupMinMax: { min: slide.ProductFlowSetup.MIN, max: slide.ProductFlowSetup.MAX },
          productFlowSetupFromSection: (slide.ProductFlowSetupFromSection._V as number) ?? 0,
          speedManualVis: slide.SpeedManualVisibility._V,
          speedFixFlowVis: slide.SpeedFixFlowVisibility._V,
          speedSectionFixFlowVis: slide.SpeedSectionFixFlowVisibility._V,
          speedPropId: slide.Vibrator.SpeedSetup._ID,
          productFlowSetupPropId: slide.ProductFlowSetup._ID,
          productFlowSetupDisabled: true,
          speedSetupDisabled: true,
          speedSetupRole: slide.Vibrator.SpeedSetup.Role
        });


        this._structureSubs.push(
          currentSection.Slides[index].Vibrator.SpeedSetup.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
            this.viewData.slides[index].speed = val;
            this.viewData.slides[index].speedSetupValueStore = val;
            this.viewData.slides[index].speedSetupDisabled = false;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].SpeedManualVisibility.pipe(takeUntil(this._unSubscriber)).subscribe((val: string) => {
            this.viewData.slides[index].speedManualVis = val;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].ProductFlowSetup.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
            this.viewData.slides[index].productFlowSetup = val;
            this.viewData.slides[index].productFlowSetupValueStore = val;
            this.viewData.slides[index].productFlowSetupDisabled = false;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].SpeedFixFlowVisibility.pipe(takeUntil(this._unSubscriber)).subscribe((val: string) => {
            this.viewData.slides[index].speedFixFlowVis = val;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].ProductFlowSetupFromSection.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
            this.viewData.slides[index].productFlowSetupFromSection = val;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].SpeedSectionFixFlowVisibility.pipe(takeUntil(this._unSubscriber)).subscribe((val: string) => {
            this.viewData.slides[index].speedSectionFixFlowVis = val;
          })
        );

        this._structureSubs.push(
          currentSection.Slides[index].PortataMediaKgOra.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
            this.viewData.slides[index].actualFlow = val;
          })
        );
      });

      // Set cameras
      this.shiftPropId = currentSectionRaw.Shift._ID;
      this.shiftRole = currentSectionRaw.Shift.Role;
      this.shiftMinMax = {
        min: currentSectionRaw.Shift.MIN,
        max: currentSectionRaw.Shift.MAX
      };

      this._structureSubs.push(
        currentSection.Shift.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.camera.delay = val;
          this.shiftValueStore = val;
          this.shiftInputDisabled = false;
        })
      );

      this.minShootPropId = currentSectionRaw.MinShoot._ID;
      this.minShootRole = currentSectionRaw.MinShoot.Role;
      this.minShootMinMax = {
        min: currentSectionRaw.MinShoot.MIN,
        max: currentSectionRaw.MinShoot.MAX
      };
      this._structureSubs.push(
        currentSection.MinShoot.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.camera.minTimeOn = val;
          this.minShootValueStore = val;
          this.minShootInputDisabled = false;
        })
      );

      this.blobHAdjustPropId = currentSectionRaw.ElabBlobHAdjust._ID;
      this.blobHRole = currentSectionRaw.ElabBlobHAdjust.Role;
      this.blobHMinMax = {
        min: currentSectionRaw.ElabBlobHAdjust.MIN,
        max: currentSectionRaw.ElabBlobHAdjust.MAX
      };
      this._structureSubs.push(
        currentSection.ElabBlobHAdjust.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.camera.growH = val;
          this.blobHAdjustValueStore = val;
          this.blobHAdjustInputDisabled = false;
        })
      );

      this.blobVAdjustPropId = currentSectionRaw.ElabBlobVAdjust._ID;
      this.blobVRole = currentSectionRaw.ElabBlobVAdjust.Role;
      this.blobVMinMax = {
        min: currentSectionRaw.ElabBlobVAdjust.MIN,
        max: currentSectionRaw.ElabBlobVAdjust.MAX
      };
      this._structureSubs.push(
        currentSection.ElabBlobVAdjust.pipe(takeUntil(this._unSubscriber)).subscribe((val: number) => {
          this.viewData.camera.growV = val;
          this.blobVAdjustValueStore = val;
          this.blobVAdjustInputDisabled = false;
        })
      );
    }
  }

  protected async onSetPropertyInputChangeByInputList(newVal: number, inputDisabled: string, oldValue: number, inputId: string, inputs?: QueryList<DxNumberBoxComponent>, propertyId?: number): Promise<void> {
    if (newVal != oldValue && inputs) {
      inputs.forEach(input => {
        const native: HTMLElement = (input as any).element.nativeElement;
        native.id
      });

      const input = inputs.find(input => (input as any).element.nativeElement.id === inputId);

      if (input) {
        this.onSetPropertyInputChange(newVal, inputDisabled, oldValue, input, propertyId);
      }
    }
  }

  protected async onSetPropertyInputChange(newVal: number, inputDisabled: string, oldValue: number, inputRef?: DxNumberBoxComponent, propertyId?: number): Promise<void> {
    if (!this._inputDisabledByString(inputDisabled) && newVal != oldValue) {
      if (propertyId && this.options?.machineName) {
        this._setInputDisabledByString(inputDisabled, true);
        //TODO: maybe validation
        const res = await this.remoteDataWrapperService.setPropertyOnSupplier(this.options.machineName, propertyId, newVal);

        if (res) {
          this._toastr.success('Operation done.', '', { timeOut: 750 });
          oldValue = newVal;
        } else {
          this._setInputDisabledByString(inputDisabled, false);
          this._toastr.error('Operation failed.', '', { timeOut: 750 });
          inputRef ? inputRef.value = oldValue : '';
        }

        return;
      }

      this._toastr.error('Operation failed.', '', { timeOut: 750 });
      inputRef ? inputRef.value = oldValue : '';
    }
  }

  private _setInputDisabledByString(inputDisabled: string, val: boolean): void {
    if (inputDisabled.split('.').length > 1) {
      if (this._hasNestedProperty(this, inputDisabled)) {
        this._nestedSetter(this, inputDisabled, val);
      }
    } else {
      if (this.hasOwnProperty(inputDisabled)) {
        (this as any)[inputDisabled] = val;
      }
    }
  }

  private _inputDisabledByString(inputDisabled: string): boolean {
    if (inputDisabled.split('.').length > 1) {
      if (this._hasNestedProperty(this, inputDisabled)) {
        return this._nestedGetter(this, inputDisabled) as boolean;
      }
    } else {
      if (this.hasOwnProperty(inputDisabled)) {
        return (this as any)[inputDisabled];
      }
    }

    return true;
  }

  protected isFenixActionEnabled(role: string | undefined): Observable<boolean> {
    if (!role) return of(false);
    return this._authHelperSvc.isFenixActionEnabled(role);
  }

  private _hasNestedProperty(obj: any, path: string): boolean {
    const keys = path.split('.'); // Rozdelenie cesty na časti
    let current = obj;

    for (const key of keys) {
      // Ak kľúč obsahuje index v poli, spracuj ho
      const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const index = +arrayMatch[2];
        if (current[arrayName] && current[arrayName][index] !== undefined) {
          current = current[arrayName][index];
        } else {
          return false;
        }
      } else {
        // Bežná vlastnosť
        if (current && current.hasOwnProperty(key)) {
          current = current[key];
        } else {
          return false;
        }
      }
    }

    return true;
  }

  private _nestedSetter(obj: any, path: string, value: any): void {
    const keys = path.split('.'); // Rozdelenie cesty na jednotlivé časti
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);

      if (arrayMatch) {
        // Spracovanie polí (napr. slides[0])
        const arrayName = arrayMatch[1];
        const index = +arrayMatch[2];

        if (!current[arrayName]) {
          current[arrayName] = []; // Ak pole neexistuje, vytvor ho
        }
        if (!current[arrayName][index]) {
          current[arrayName][index] = {}; // Ak objekt na indexe neexistuje, vytvor ho
        }

        current = current[arrayName][index];
      } else {
        // Spracovanie bežných vlastností
        if (!current[key]) {
          current[key] = {}; // Ak vlastnosť neexistuje, vytvor ju
        }

        current = current[key];
      }
    }

    // Nastavenie hodnoty na poslednej vlastnosti
    const lastKey = keys[keys.length - 1];
    current[lastKey] = value;
  }

  private _nestedGetter(obj: any, path: string): any {
    const keys = path.split('.'); // Rozdelenie cesty na jednotlivé časti
    let current = obj;

    for (const key of keys) {
      const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);

      if (arrayMatch) {
        // Spracovanie polí (napr. slides[0])
        const arrayName = arrayMatch[1];
        const index = +arrayMatch[2];

        if (!current[arrayName] || current[arrayName][index] === undefined) {
          return undefined; // Vráti undefined, ak cesta neexistuje
        }

        current = current[arrayName][index];
      } else {
        // Bežná vlastnosť
        if (!current || current[key] === undefined) {
          return undefined; // Vráti undefined, ak cesta neexistuje
        }

        current = current[key];
      }
    }

    return current; // Vráti hodnotu na konci cesty
  }

}
